import React from 'react';

import MomentUtils from '@date-io/moment';
import { FormControlLabel, Grid, IconButton, Radio, RadioGroup } from '@material-ui/core';
import { createStyles, withStyles, WithStyles } from '@material-ui/core/styles';
import { ExpandLess } from '@material-ui/icons';
import classnames from 'classnames';
import { LumaTheme } from 'embeds/core/styles/getTheme';
import { InlineDatePicker, InlineDateTimePicker, MuiPickersUtilsProvider } from 'material-ui-pickers';
import moment from 'moment';

import { Typography } from 'components/v2';

import { useFiltersContext, useTabContext } from '../context/FixedFiltersContext';

const styles = ({ custom: { fontSizes }, spacing: { unit } }: LumaTheme) =>
  createStyles({
    radioGroup: {
      padding: `${unit * 2}px ${unit * 2}px 0 ${unit * 2}px`,
    },
    radio: {
      fontSize: fontSizes.body,
    },
    customDateRange: {
      display: 'flex',
      alignItems: 'center',
      width: '100%',
      height: 46,
      padding: `0px ${unit * 2}px`,
    },
    rangeTextWrapper: {
      margin: `0 ${unit * 2}px`,
    },
    textFieldWrapper: {
      width: 220,
    },
    textFieldInput: {
      fontSize: fontSizes.small,
      height: '36px !important',
    },
    textFieldHelperError: {
      position: 'absolute',
      top: 36,
    },
    padding: {
      padding: `${unit}px ${1.5 * unit}px`,
      paddingTop: 0,
    },
    readOnly: {
      pointerEvents: 'none',
    },
  });

export enum AppointmentDateEnum {
  Any = 'any',
  SpecificDate = 'specificDate',
  AfterSpecificDate = 'afterSpecificDate',
  DateRange = 'custom',
}

const getFromAndToDateFromValue = (value: string) => {
  switch (value) {
    case AppointmentDateEnum.AfterSpecificDate:
      return {
        fromDate: moment()
          .endOf('day')
          .valueOf(),
        toDate: null,
      };
    case AppointmentDateEnum.SpecificDate:
      return {
        fromDate: moment()
          .startOf('day')
          .valueOf(),
        toDate: moment()
          .endOf('day')
          .valueOf(),
      };
    case AppointmentDateEnum.Any:
      return {
        fromDate: null,
        toDate: null,
      };
    case AppointmentDateEnum.DateRange:
      return {
        fromDate: moment()
          .subtract(7, 'day')
          .startOf('day')
          .valueOf(),
        toDate: moment()
          .startOf('day')
          .valueOf(),
      };
    default:
      return {
        fromDate: null,
        toDate: null,
      };
  }
};

export const APPOINTMENT_DATE_KEY = 'apptDate';

export interface FiltersAppointmentDateProps extends WithStyles<typeof styles> {}

const FiltersAppointmentDate: React.FC<FiltersAppointmentDateProps> = ({ classes }) => {
  const { filters, updater, readOnly } = useFiltersContext();
  const { changeTab } = useTabContext();
  const appointmentDateFilter = filters[APPOINTMENT_DATE_KEY];
  const selectedDate = appointmentDateFilter.selectedDate;

  const handleDateChangee = (value: any) =>
    handleCustomRange({ dateOption: selectedDate, dateField: 'fromDate', value });

  const handleRadioClick = (value: string) => {
    updater(APPOINTMENT_DATE_KEY)({
      selectedDate: value,
      ...getFromAndToDateFromValue(value),
    });
  };

  const handleCustomRange = ({ dateOption, dateField, value }: any) => {
    updater(APPOINTMENT_DATE_KEY)({
      ...appointmentDateFilter,
      selectedDate: dateOption,
      [dateField]: value.valueOf(),
      ...(dateOption === 'specificDate' && { toDate: value.endOf('day').valueOf() }), // add end of day for specificDate
    });
  };

  return (
    <div
      className={classnames({
        [classes.readOnly]: readOnly,
      })}
    >
      <Grid container justify="space-between" alignItems="center">
        <Grid item>
          <RadioGroup
            className={classes.radioGroup}
            name="spacing"
            aria-label="Spacing"
            value={appointmentDateFilter.selectedDate}
            onChange={(_, value) => handleRadioClick(value)}
            row
          >
            <FormControlLabel
              classes={{ label: classes.radio, root: classes.padding }}
              value="any"
              control={<Radio color="primary" />}
              label="Any"
            />
            <FormControlLabel
              classes={{ label: classes.radio, root: classes.padding }}
              value="specificDate"
              control={<Radio color="primary" />}
              label="Specific Date"
            />
            <FormControlLabel
              classes={{ label: classes.radio, root: classes.padding }}
              value="afterSpecificDate"
              control={<Radio color="primary" />}
              label="After Specific Date"
            />
            <FormControlLabel
              classes={{ label: classes.radio, root: classes.padding }}
              value="custom"
              control={<Radio color="primary" />}
              label="Custom Range"
            />
          </RadioGroup>
        </Grid>
        <Grid item>
          <IconButton onClick={() => changeTab({ tab: null, index: false })}>
            <ExpandLess />
          </IconButton>
        </Grid>
      </Grid>
      {/* make so that for specific date and after specific date we only have date and no time */}
      {selectedDate === 'specificDate' && (
        <div className={classes.customDateRange}>
          <div className={classes.textFieldWrapper}>
            <MuiPickersUtilsProvider utils={MomentUtils}>
              <InlineDatePicker
                keyboard
                value={appointmentDateFilter.fromDate}
                onChange={handleDateChangee}
                format="MM/DD/YYYY"
                InputProps={{
                  classes: {
                    input: classes.textFieldInput,
                  },
                }}
                FormHelperTextProps={{
                  classes: {
                    error: classes.textFieldHelperError,
                  },
                }}
              />
            </MuiPickersUtilsProvider>
          </div>
        </div>
      )}
      {selectedDate === 'afterSpecificDate' && (
        <div className={classes.customDateRange}>
          <div className={classes.textFieldWrapper}>
            <MuiPickersUtilsProvider utils={MomentUtils}>
              <InlineDateTimePicker
                keyboard
                value={appointmentDateFilter.fromDate}
                onChange={handleDateChangee}
                format="MM/DD/YYYY hh:mm A"
                InputProps={{
                  classes: {
                    input: classes.textFieldInput,
                  },
                }}
                FormHelperTextProps={{
                  classes: {
                    error: classes.textFieldHelperError,
                  },
                }}
              />
            </MuiPickersUtilsProvider>
          </div>
        </div>
      )}
      {/* custom range is the only one that deals with date and time */}
      {selectedDate === 'custom' && (
        <div className={classes.customDateRange}>
          <div className={classes.textFieldWrapper}>
            <MuiPickersUtilsProvider utils={MomentUtils}>
              <InlineDateTimePicker
                keyboard
                ampm
                showTabs={false}
                value={appointmentDateFilter.fromDate}
                onChange={handleDateChangee}
                format="MM/DD/YYYY hh:mm A"
                InputProps={{
                  classes: {
                    input: classes.textFieldInput,
                  },
                }}
                FormHelperTextProps={{
                  classes: {
                    error: classes.textFieldHelperError,
                  },
                }}
              />
            </MuiPickersUtilsProvider>
          </div>
          <React.Fragment>
            <div className={classes.rangeTextWrapper}>
              <Typography variant="body2">to</Typography>
            </div>
            <div className={classes.textFieldWrapper}>
              <MuiPickersUtilsProvider utils={MomentUtils}>
                <InlineDateTimePicker
                  keyboard
                  ampm
                  showTabs={false}
                  value={filters.apptDate.toDate}
                  onChange={(value) => handleCustomRange({ dateOption: selectedDate, dateField: 'toDate', value })}
                  minDate={appointmentDateFilter.fromDate}
                  minDateMessage="Invalid Range"
                  format="MM/DD/YYYY hh:mm A"
                  InputProps={{
                    classes: {
                      input: classes.textFieldInput,
                    },
                  }}
                  FormHelperTextProps={{
                    classes: {
                      error: classes.textFieldHelperError,
                    },
                  }}
                />
              </MuiPickersUtilsProvider>
            </div>
          </React.Fragment>
        </div>
      )}
    </div>
  );
};

export default withStyles(styles)(FiltersAppointmentDate);
